import type { OrganizationMember } from "@common/dto/organization/organization-member.dto";
import type {
    WorkspaceDetails,
    WorkspaceMember,
} from "@common/types/workspace";
import {
    UserWorkspaceRole,
    userWorkspaceRolesInOrder,
} from "@common/userWorkspaceRoles";

export function organizationMemberHasWorkspaceAccess(
    member: OrganizationMember,
    workspaceId: string,
): boolean {
    return member.workspaceAssignments.some(
        (a) => a.workspaceId === workspaceId,
    );
}

export function filterOrganizationMembersByWorkspace(
    members: OrganizationMember[],
    workspaceId: string,
): OrganizationMember[] {
    return members.filter((m) =>
        organizationMemberHasWorkspaceAccess(m, workspaceId),
    );
}

export type WorkspaceMemberForClientAssignment = Pick<
    WorkspaceMember,
    "user" | "role" | "organizationId"
>;

const WORKSPACE_ROLE_PRIORITY = Object.fromEntries(
    userWorkspaceRolesInOrder.map((role, index) => [role, index]),
) as Record<UserWorkspaceRole, number>;

function pickBestRoleUserId(
    workspaceMembers: WorkspaceMemberForClientAssignment[],
): number | null {
    if (workspaceMembers.length === 0) {
        return null;
    }
    const bestMember = workspaceMembers.reduce(
        (acc, member) =>
            WORKSPACE_ROLE_PRIORITY[member.role] <
            WORKSPACE_ROLE_PRIORITY[acc.role]
                ? member
                : acc,
        workspaceMembers[0],
    );
    return bestMember.user.id;
}

export function buildWorkspaceMembersMap(
    workspaceIds: string[],
    workspaceDetailsList: WorkspaceDetails[] | undefined,
): Record<string, WorkspaceMemberForClientAssignment[]> {
    const map: Record<string, WorkspaceMemberForClientAssignment[]> =
        Object.fromEntries(
            workspaceIds.map((workspaceId) => [workspaceId, []]),
        );

    workspaceDetailsList?.forEach((workspaceDetails) => {
        const workspaceId = workspaceDetails.workspace.id;
        if (!(workspaceId in map)) {
            return;
        }
        map[workspaceId] = workspaceDetails.assignments.map((assignment) => ({
            user: assignment.user,
            role: assignment.role,
            organizationId: assignment.organizationId,
        }));
    });

    return map;
}

export function computeDefaultClientIdsByWorkspaceId(
    workspaceMembersMap: Record<string, WorkspaceMemberForClientAssignment[]>,
): Record<string, number | null> {
    return Object.fromEntries(
        Object.entries(workspaceMembersMap).map(
            ([workspaceId, workspaceMembers]) => {
                const nonOrgMembers = workspaceMembers.filter(
                    (member) => member.organizationId === null,
                );
                const preferredUserId =
                    pickBestRoleUserId(nonOrgMembers) ??
                    pickBestRoleUserId(workspaceMembers);
                return [workspaceId, preferredUserId];
            },
        ),
    );
}

interface GetWorkspaceMemberDisplayNameParams {
    workspaceMembersMap: Record<string, WorkspaceMemberForClientAssignment[]>;
    workspaceId: string;
    userId: number | null;
    fallback: (userId: number) => string;
}

export function getWorkspaceMemberDisplayName({
    workspaceMembersMap,
    workspaceId,
    userId,
    fallback,
}: GetWorkspaceMemberDisplayNameParams): string {
    if (userId == null) {
        return "";
    }
    const workspaceMembers = workspaceMembersMap[workspaceId] ?? [];
    const workspaceMember =
        workspaceMembers.find((m) => m.user.id === userId) ?? null;
    if (workspaceMember) {
        const fullName =
            `${workspaceMember.user.firstName ?? ""} ${workspaceMember.user.lastName ?? ""}`.trim();
        if (fullName) {
            return fullName;
        }
    }
    return fallback(userId);
}

/** Members who can access at least one of the given workspaces (wizard defaults step). */
export function filterOrganizationMembersWithAnyWorkspaceAccess(
    members: OrganizationMember[],
    workspaceIds: string[],
): OrganizationMember[] {
    if (workspaceIds.length === 0) {
        return [];
    }
    const workspaceIdSet = new Set(workspaceIds);
    return members.filter((m) =>
        m.workspaceAssignments.some((a) => workspaceIdSet.has(a.workspaceId)),
    );
}
